import { TOKEN_PROGRAM_ID } from '@solana/spl-token'
import { TokenMetadataItem } from 'types/token'
import { LastPrice } from 'utils/tokens'
import { SearchResult } from 'hooks/useSearchResults'
import {
  MarketOverview,
  SortByKey,
} from 'hooks/useMarketOverview'
import { FiltersForm } from '@components/markets/TokenFiltersForm'
import { TokenOverview } from 'hooks/useBirdeyeTokenOverview'
import { TokenPairSummaryResponse } from 'hooks/useTokenPairSummary'
import { birdeyeFetch } from './helpers'

// --- Birdeye limits (per docs) -------------------------------------------
// multi_price tolerates up to 100 mints per call
// meta-data/multiple tolerates up to 50 mints per call
const PRICE_CHUNK_SIZE = 100
const METADATA_CHUNK_SIZE = 50

// Default token program for results that lack explicit info.
// ~99% of Solana SPL tokens use the legacy program; Token-2022 detection
// happens lazily in fetchMetadataForMints' on-chain fallback path.
const DEFAULT_TOKEN_PROGRAM = TOKEN_PROGRAM_ID.toBase58()

// --- Birdeye response shapes ---------------------------------------------

interface BirdeyeMultiPriceItem {
  value: number | null
  updateUnixTime?: number
  priceChange24h?: number
  liquidity?: number
}

type BirdeyeMultiPriceData = Record<string, BirdeyeMultiPriceItem | null>

interface BirdeyeMetadataExtensions {
  coingecko_id?: string | null
  website?: string | null
  twitter?: string | null
  telegram?: string | null
  description?: string | null
  discord?: string | null
  medium?: string | null
}

interface BirdeyeMetadataItem {
  address: string
  symbol: string
  name: string
  decimals: number
  logo_uri?: string | null
  extensions?: BirdeyeMetadataExtensions | null
}

type BirdeyeMetadataMultipleData = Record<string, BirdeyeMetadataItem | null>

interface BirdeyeSearchTokenResult {
  address: string
  symbol: string
  name: string
  logo_uri?: string | null
  price?: number | null
  volume_24h_usd?: number | null
  // Birdeye search endpoint returns trade_24h (NOT trade_24h_count — that
  // naming is used by the v3 token list endpoint).
  trade_24h?: number | null
}

interface BirdeyeSearchSection {
  type: string
  result?: BirdeyeSearchTokenResult[] | null
}

interface BirdeyeSearchData {
  items?: BirdeyeSearchSection[] | null
}

interface BirdeyeTrendingToken {
  address: string
  symbol: string
  name: string
  decimals: number
  logoURI?: string | null
  price?: number | null
  volume24hUSD?: number | null
}

interface BirdeyeTrendingData {
  tokens?: BirdeyeTrendingToken[] | null
}

interface BirdeyeTokenListItem {
  address: string
  symbol: string
  name: string
  decimals: number
  logo_uri?: string | null
  price?: number | null
  liquidity?: number | null
  volume_24h_usd?: number | null
  trade_24h_count?: number | null
  price_change_5m_percent?: number | null
  price_change_1h_percent?: number | null
  price_change_6h_percent?: number | null
  price_change_24h_percent?: number | null
  total_supply?: number | null
  circulating_supply?: number | null
  recent_listing_time?: number | null
}

interface BirdeyeTokenListData {
  items?: BirdeyeTokenListItem[] | null
  tokens?: BirdeyeTokenListItem[] | null
}

// --- Helpers --------------------------------------------------------------

const dedupe = (mints: string[]): string[] => Array.from(new Set(mints))

async function chunkAndParallel<T>(
  items: string[],
  chunkSize: number,
  worker: (chunk: string[]) => Promise<T[]>,
): Promise<T[]> {
  if (items.length === 0) return []
  if (items.length <= chunkSize) return worker(items)

  const chunks: string[][] = []
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize))
  }
  const results = await Promise.all(chunks.map(worker))
  return results.flat()
}

// --- Public adapter API ---------------------------------------------------

export async function fetchBirdeyePricesForMints(
  mints: string[],
): Promise<LastPrice[]> {
  const unique = dedupe(mints).filter(Boolean)
  if (unique.length === 0) return []

  return chunkAndParallel(unique, PRICE_CHUNK_SIZE, async (chunk) => {
    const path = `defi/multi_price?list_address=${chunk.join(
      ',',
    )}&include_liquidity=false`
    const response = await birdeyeFetch<BirdeyeMultiPriceData>(path)
    if (!response.success || !response.data) return []

    const out: LastPrice[] = []
    for (const [mint, item] of Object.entries(response.data)) {
      if (item && typeof item.value === 'number' && item.value > 0) {
        out.push({ mint, price: item.value })
      }
    }
    return out
  })
}

export async function fetchBirdeyeMetadataForMints(
  mints: string[],
): Promise<TokenMetadataItem[]> {
  const unique = dedupe(mints).filter(Boolean)
  if (unique.length === 0) return []

  // Fire metadata + price in parallel — the legacy Traffic /token endpoint
  // returned both in one shot, so callers expect populated `latest_price_usd`.
  const [metadataItems, prices] = await Promise.all([
    chunkAndParallel(unique, METADATA_CHUNK_SIZE, async (chunk) => {
      const path = `defi/v3/token/meta-data/multiple?list_address=${chunk.join(
        ',',
      )}`
      const response = await birdeyeFetch<BirdeyeMetadataMultipleData>(path)
      if (!response.success || !response.data) return []
      return Object.values(response.data).filter(
        (x): x is BirdeyeMetadataItem => !!x && typeof x.address === 'string',
      )
    }),
    fetchBirdeyePricesForMints(unique),
  ])

  const priceByMint = new Map(prices.map((p) => [p.mint, p.price]))

  return metadataItems.map((m) => birdeyeMetadataToTokenItem(m, priceByMint))
}

function birdeyeMetadataToTokenItem(
  m: BirdeyeMetadataItem,
  priceByMint: Map<string, number>,
): TokenMetadataItem {
  const ext = m.extensions ?? {}
  return {
    mint: m.address,
    symbol: m.symbol ?? '',
    name: m.name ?? '',
    decimals: m.decimals ?? 0,
    image_url: m.logo_uri ?? '',
    latest_price_usd: priceByMint.get(m.address) ?? 0,
    description: ext.description ?? null,
    twitter: ext.twitter ?? null,
    telegram: ext.telegram ?? null,
    coingecko_id: ext.coingecko_id ?? null,
    website: ext.website ?? null,
    // Birdeye metadata endpoint doesn't return per-token volume/trade stats.
    // Consumers needing these (search, market list) call the dedicated
    // search/list endpoints which DO return them.
    volume_24h_usd: 0,
    amount_24h: 0,
    trades_count_24h: 0,
    token_program: DEFAULT_TOKEN_PROGRAM,
  }
}

// --- Search & discovery ---------------------------------------------------

export async function fetchBirdeyeSearch(
  searchTerm: string,
  limit = 10,
): Promise<SearchResult[]> {
  const trimmed = searchTerm.trim()
  if (trimmed.length < 1) return []

  const params = new URLSearchParams({
    keyword: trimmed,
    chain: 'solana',
    target: 'token',
    search_mode: 'fuzzy',
    search_by: 'combination',
    sort_by: 'volume_24h_usd',
    sort_type: 'desc',
    offset: '0',
    limit: String(limit),
  })
  const path = `defi/v3/search?${params.toString()}`
  const response = await birdeyeFetch<BirdeyeSearchData>(path)
  if (!response.success || !response.data?.items?.length) return []

  const tokenSection = response.data.items.find(
    (section) => section.type === 'token',
  )
  if (!tokenSection?.result?.length) return []

  return tokenSection.result.map(birdeyeSearchToSearchResult)
}

function birdeyeSearchToSearchResult(
  t: BirdeyeSearchTokenResult,
): SearchResult {
  return {
    mint: t.address,
    symbol: t.symbol ?? '',
    name: t.name ?? '',
    image_url: t.logo_uri ?? undefined,
    latest_price_usd: t.price ?? 0,
    volume_24h_usd: t.volume_24h_usd ?? 0,
    trades_count_24h: t.trade_24h ?? 0,
    amount_24h: 0,
  }
}

export async function fetchBirdeyeTrending(limit = 20): Promise<SearchResult[]> {
  const params = new URLSearchParams({
    sort_by: 'rank',
    sort_type: 'asc',
    offset: '0',
    limit: String(limit),
  })
  const path = `defi/token_trending?${params.toString()}`
  const response = await birdeyeFetch<BirdeyeTrendingData>(path)
  if (!response.success || !response.data?.tokens?.length) return []

  return response.data.tokens.map(birdeyeTrendingToSearchResult)
}

function birdeyeTrendingToSearchResult(t: BirdeyeTrendingToken): SearchResult {
  return {
    mint: t.address,
    symbol: t.symbol ?? '',
    name: t.name ?? '',
    image_url: t.logoURI ?? undefined,
    latest_price_usd: t.price ?? 0,
    volume_24h_usd: t.volume24hUSD ?? 0,
    trades_count_24h: 0,
    amount_24h: 0,
  }
}

// --- Markets list ---------------------------------------------------------

const SORT_KEY_MAP: Record<SortByKey, string> = {
  volume: 'volume_24h_usd',
  change: 'price_change_24h_percent',
  trades: 'trade_24h_count',
  newest: 'recent_listing_time',
}

export async function fetchBirdeyeTokenList(args: {
  sortBy: SortByKey
  filters: FiltersForm
  pageSize: number
  pageIndex: number
  quoteToken: 'USDC' | 'SOL' | string
  solPrice: number
}): Promise<MarketOverview[]> {
  const { sortBy, filters, pageSize, pageIndex, quoteToken, solPrice } = args

  const params = new URLSearchParams({
    sort_by: SORT_KEY_MAP[sortBy],
    sort_type: 'desc',
    offset: String(pageSize * pageIndex),
    limit: String(pageSize),
  })
  // Birdeye requires a min_liquidity filter to avoid junk; default to a
  // sensible floor and let user filters tighten it.
  params.set('min_liquidity', '1000')
  if (filters.min_quote_volume.min) {
    params.set('min_volume_24h_usd', filters.min_quote_volume.min)
  }
  if (filters.min_trades.min) {
    params.set('min_trade_24h_count', filters.min_trades.min)
  }

  const path = `defi/v3/token/list?${params.toString()}`
  const response = await birdeyeFetch<BirdeyeTokenListData>(path)
  if (!response.success || !response.data) return []

  const items = response.data.items ?? response.data.tokens ?? []
  const quoteIsSol = quoteToken === 'SOL'

  return items.map((t) =>
    birdeyeListToMarketOverview(t, quoteIsSol ? solPrice : 1),
  )
}

function birdeyeListToMarketOverview(
  t: BirdeyeTokenListItem,
  quoteUsdPrice: number,
): MarketOverview {
  const usdVolume = t.volume_24h_usd ?? 0
  const usdPrice = t.price ?? 0
  // The legacy MarketOverview shape stored values in QUOTE units (so the
  // markets table could render "price in SOL" directly when in lamport mode).
  // For SOL-quote: divide USD by SOL price. For USDC-quote: USD == quote.
  const inQuote = (usd: number) =>
    quoteUsdPrice && quoteUsdPrice !== 1 ? usd / quoteUsdPrice : usd
  // Birdeye returns percentages as e.g. 5.2 for 5.2%. The consumer (TokensTable
  // and the rug filter) expects decimal form (0.052), so divide by 100.
  const toDecimal = (pct: number | null | undefined): number | undefined =>
    pct == null ? undefined : pct / 100
  return {
    base_token_mint: t.address,
    base_token_name: t.name ?? '',
    base_token_symbol: t.symbol ?? '',
    base_token_image_url: t.logo_uri ?? undefined,
    base_token_supply: t.total_supply ?? t.circulating_supply ?? undefined,
    latest_price: inQuote(usdPrice),
    price_change_5m: toDecimal(t.price_change_5m_percent),
    price_change_1h: toDecimal(t.price_change_1h_percent),
    price_change_6h: toDecimal(t.price_change_6h_percent),
    price_change_24h: toDecimal(t.price_change_24h_percent),
    quote_volume_24h: inQuote(usdVolume),
    trades_count_24h: t.trade_24h_count ?? 0,
    volume_usd_24h: usdVolume,
  }
}

// --- Token pair summary --------------------------------------------------
// Adapts Birdeye's token_overview response into the legacy TokenPairSummary
// shape so consumers (TokenChangeStats, MarketHeroStats) keep working.
//
// Period mapping notes:
//   - m5: Birdeye's smallest bucket is 30m. We expose 30m data under the m5
//     key so the existing UI label still has a value.
//   - h1: exact 1h match.
//   - h6: priceChange6hPercent is exact, but Birdeye doesn't expose buy/sell
//     counts or volumes for a 6h window. We use the 8h window for those
//     (closest available) and accept that the volume number covers a slightly
//     wider period than the price change.
//   - d1: exact 24h match.
//
// Volumes are USD-denominated (Birdeye's vBuy*USD / vSell*USD), unlike the
// Traffic shape which returned quote-currency volumes.
export function birdeyeOverviewToPairSummary(
  overview: TokenOverview | undefined,
  baseMint: string,
  quoteMint: string,
): TokenPairSummaryResponse | undefined {
  if (!overview) return undefined

  const fromPercent = (pct: number | null | undefined): number | null =>
    pct == null ? null : pct / 100

  return {
    base: { mint: baseMint },
    quote: { mint: quoteMint },
    summary: {
      m5: {
        price: { change: fromPercent(overview.priceChange30mPercent) ?? 0 },
        amount: { buy: { amount: 0 }, sell: { amount: 0 } },
        trades: {
          buy: { trades: overview.buy30m ?? 0 },
          sell: { trades: overview.sell30m ?? 0 },
        },
        volume: {
          buy: { volume: overview.vBuy30mUSD ?? 0 },
          sell: { volume: overview.vSell30mUSD ?? 0 },
        },
      },
      h1: {
        price: { change: fromPercent(overview.priceChange1hPercent) ?? 0 },
        amount: { buy: { amount: 0 }, sell: { amount: 0 } },
        trades: {
          buy: { trades: overview.buy1h ?? 0 },
          sell: { trades: overview.sell1h ?? 0 },
        },
        volume: {
          buy: { volume: overview.vBuy1hUSD ?? 0 },
          sell: { volume: overview.vSell1hUSD ?? 0 },
        },
      },
      h6: {
        price: { change: fromPercent(overview.priceChange6hPercent) ?? 0 },
        amount: { buy: { amount: 0 }, sell: { amount: 0 } },
        trades: {
          buy: { trades: overview.buy8h ?? 0 },
          sell: { trades: overview.sell8h ?? 0 },
        },
        volume: {
          buy: { volume: overview.vBuy8hUSD ?? 0 },
          sell: { volume: overview.vSell8hUSD ?? 0 },
        },
      },
      d1: {
        price: { change: fromPercent(overview.priceChange24hPercent) ?? 0 },
        amount: { buy: { amount: 0 }, sell: { amount: 0 } },
        trades: {
          buy: { trades: overview.buy24h ?? 0 },
          sell: { trades: overview.sell24h ?? 0 },
        },
        volume: {
          buy: { volume: overview.vBuy24hUSD ?? 0 },
          sell: { volume: overview.vSell24hUSD ?? 0 },
        },
      },
    },
  }
}
